import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Package, 
  GraduationCap, 
  Calendar, 
  MessageCircle, 
  CheckCircle2, 
  Clock, 
  Search
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface UserProduct {
  id: string;
  title: string;
  product_type: string;
  status: 'active' | 'completed' | 'expired' | 'refunded' | string;
  payment_gateway: string;
  amount_paid: number;
  starts_at: string;
  expires_at: string | null;
  mentor_id?: string;
  mentor?: {
    id: string;
    name: string;
    title: string;
    avatar_url: string;
    contact_email?: string;
    whatsapp?: string;
    checkout_url?: string;
  };
}

interface MyProductsPageProps {
  onBack?: () => void;
  onNavigate?: (section: string, subSection?: string) => void;
}

export const MyProductsPage: React.FC<MyProductsPageProps> = ({ onBack, onNavigate }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUserProducts();
  }, [user]);

  const fetchUserProducts = async () => {
    setLoading(true);
    try {
      if (!user) {
        setProducts(getMockUserProducts());
        setLoading(false);
        return;
      }

      // Buscar produtos do usuário autenticado no Supabase
      const { data, error } = await supabase
        .from('user_products')
        .select(`
          *,
          mentor:mentors(id, name, title, avatar_url, contact_email, checkout_url)
        `)
        .or(`user_id.eq.${user.id},buyer_email.ilike.${user.email}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Não foi possível carregar produtos reais do Supabase, utilizando dados de demonstração:', error.message);
        setProducts(getMockUserProducts());
      } else if (data && data.length > 0) {
        setProducts(data as UserProduct[]);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setProducts(getMockUserProducts());
    } finally {
      setLoading(false);
    }
  };

  // Dados de fallback para demonstração
  const getMockUserProducts = (): UserProduct[] => [
    {
      id: 'demo-1',
      title: 'Mentoria Individual Elite 1:1 - Scalable SaaS Architect',
      product_type: 'mentorship',
      status: 'active',
      payment_gateway: 'kirvano',
      amount_paid: 1497.00,
      starts_at: new Date(Date.now() - 15 * 86400000).toISOString(),
      expires_at: new Date(Date.now() + 350 * 86400000).toISOString(),
      mentor: {
        id: 'mentor-1',
        name: 'Vitor Terra',
        title: 'Founder MasterClub & Software Architect',
        avatar_url: 'https://meqeeisqsfqtplyqqxhv.supabase.co/storage/v1/object/public/mentorias/1788524785363_vytsnw.webp',
        contact_email: 'contato@masterclub.com.br',
        checkout_url: 'https://kirvano.com'
      }
    },
    {
      id: 'demo-2',
      title: 'Aceleração de Carreira Tech & Coprodução Digital',
      product_type: 'mentorship',
      status: 'active',
      payment_gateway: 'cakto',
      amount_paid: 997.00,
      starts_at: new Date(Date.now() - 40 * 86400000).toISOString(),
      expires_at: new Date(Date.now() + 325 * 86400000).toISOString(),
      mentor: {
        id: 'mentor-2',
        name: 'Ana Costa',
        title: 'Tech Lead & Growth Hacker',
        avatar_url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400',
        contact_email: 'ana@masterclub.com.br'
      }
    }
  ];

  const filteredProducts = products.filter(product => {
    return (
      product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.mentor?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Acesso Vitalício';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getDaysRemaining = (expiresAtStr: string | null) => {
    if (!expiresAtStr) return null;
    const expiresAt = new Date(expiresAtStr).getTime();
    const now = new Date().getTime();
    const diffDays = Math.ceil((expiresAt - now) / (1000 * 3600 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return (
    <div className="min-h-screen bg-[#121316] text-white pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Simplificado */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1f2329] pb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-white">
              Minhas Compras
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Gerencie suas mentorias contratadas e acessos na MasterClub.
            </p>
          </div>

          {/* Search Box Simplificado */}
          {products.length > 0 && (
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por mentoria ou mentor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#1a1d21] text-white text-sm pl-9 pr-4 py-2.5 rounded-xl border border-gray-700 focus:border-[#0FFF55] focus:outline-none transition-colors"
              />
            </div>
          )}
        </div>

        {/* Products Grid / Empty State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="w-12 h-12 border-4 border-[#0FFF55] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 text-sm">Carregando seus produtos adquiridos...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-[#1a1d21] border border-gray-800 rounded-3xl p-12 text-center space-y-6 max-w-xl mx-auto my-8">
            <div className="w-20 h-20 bg-[#0FFF55]/10 rounded-full flex items-center justify-center mx-auto text-[#0FFF55]">
              <Package size={40} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Nenhum produto encontrado</h3>
              <p className="text-gray-400 text-sm">
                {searchTerm
                  ? 'Nenhum produto corresponde à busca pesquisada.'
                  : 'Você ainda não possui mentoria ou produto contratado em seu perfil. Conecte-se com nossos mentores parceiros e acelere seus resultados!'}
              </p>
            </div>
            <button
              onClick={() => onNavigate ? onNavigate('mentorias') : null}
              className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold px-6 py-3 rounded-xl hover:scale-105 transition-transform"
            >
              Ver Mentores Disponíveis
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredProducts.map((product) => {
              const daysRemaining = getDaysRemaining(product.expires_at);

              return (
                <div 
                  key={product.id}
                  className="bg-[#1a1d21] border border-gray-800 rounded-3xl p-6 hover:border-[#0FFF55]/40 transition-all duration-300 shadow-xl flex flex-col justify-between space-y-6 group"
                >
                  {/* Top info */}
                  <div className="space-y-4">
                    {/* Status Badge & Gateway */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="bg-[#0FFF55]/10 text-[#0FFF55] text-xs font-bold px-3 py-1 rounded-full border border-[#0FFF55]/20 flex items-center space-x-1">
                          <CheckCircle2 size={12} />
                          <span className="uppercase">{product.status === 'active' ? 'Ativo' : product.status}</span>
                        </span>
                        <span className="bg-gray-800 text-gray-400 text-xs font-semibold px-2.5 py-1 rounded-full uppercase">
                          {product.payment_gateway}
                        </span>
                      </div>

                      {daysRemaining !== null && (
                        <span className={`text-xs font-medium flex items-center space-x-1 ${daysRemaining < 30 ? 'text-amber-400' : 'text-gray-400'}`}>
                          <Clock size={12} />
                          <span>{daysRemaining} dias restantes</span>
                        </span>
                      )}
                    </div>

                    {/* Product Title */}
                    <h3 className="text-xl font-bold text-white group-hover:text-[#0FFF55] transition-colors line-clamp-2">
                      {product.title}
                    </h3>

                    {/* Mentor Info */}
                    {product.mentor && (
                      <div className="flex items-center space-x-3 bg-[#121316] p-3 rounded-2xl border border-gray-800">
                        <Avatar className="w-11 h-11 ring-2 ring-[#0FFF55]/30">
                          <AvatarImage src={product.mentor.avatar_url} />
                          <AvatarFallback className="bg-[#0FFF55] text-[#121316] font-bold">
                            {product.mentor.name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-semibold text-sm">{product.mentor.name}</p>
                          <p className="text-gray-400 text-xs">{product.mentor.title}</p>
                        </div>
                      </div>
                    )}

                    {/* Expiration and Details */}
                    <div className="grid grid-cols-2 gap-2 text-xs bg-gray-900/40 p-3 rounded-xl border border-gray-800/80">
                      <div>
                        <span className="text-gray-500 block">Adquirido em:</span>
                        <span className="text-gray-300 font-medium">{formatDate(product.starts_at)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Validade do Acesso:</span>
                        <span className="text-[#0FFF55] font-semibold">{formatDate(product.expires_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-2 border-t border-gray-800 space-y-2">
                    <button
                      onClick={() => onNavigate ? onNavigate('mentoria-hub') : null}
                      className="w-full bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:shadow-lg hover:shadow-[#0FFF55]/20 hover:scale-[1.02] transition-all"
                    >
                      <GraduationCap size={18} />
                      <span>Acessar Sala / Hub de Mentoria</span>
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          const email = product.mentor?.contact_email || 'suporte@masterclub.com.br';
                          window.open(`mailto:${email}?subject=Contato Mentoria MasterClub`);
                        }}
                        className="w-full bg-[#121316] text-gray-300 hover:text-white hover:bg-gray-800 border border-gray-700 py-2.5 px-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs font-semibold transition-colors"
                      >
                        <MessageCircle size={14} className="text-[#0FFF55]" />
                        <span>Falar com Mentor</span>
                      </button>

                      <button
                        onClick={() => {
                          if (product.mentor?.checkout_url) {
                            window.open(product.mentor.checkout_url, '_blank');
                          } else {
                            onNavigate?.('mentoria-hub');
                          }
                        }}
                        className="w-full bg-[#121316] text-gray-300 hover:text-white hover:bg-gray-800 border border-gray-700 py-2.5 px-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs font-semibold transition-colors"
                      >
                        <Calendar size={14} className="text-blue-400" />
                        <span>Detalhes / Agendar</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProductsPage;
